export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n < 1024 * 1024 * 1024 * 1024) return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
  return `${(n / 1024 / 1024 / 1024 / 1024).toFixed(2)} TB`;
}

/** Data e hora local curtas (pt-BR), para metadados na UI. */
export function formatDateTime(iso: string | null | undefined): string {
  if (iso == null || iso === "") return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

/** Data e hora com segundos (dd/mm/yyyy HH:mm:ss), para timelines de eventos. */
export function formatDateTimeSec(iso: string | null | undefined): string {
  if (iso == null || iso === "") return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

export function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h atrás`;
  return d.toLocaleDateString();
}

// ---- F-A.7: availability badge helpers ----

import type { AssetStatus, ViewerKind } from "./api";

export function availabilityLabel(s: AssetStatus): string {
  switch (s) {
    case "ready": return "Renderizado";
    case "queued": return "Em fila";
    case "rendering": return "Renderizando…";
    case "preparing": return "Preparando";
    case "error": return "Erro no render";
    // F-GC.4: os bytes saíram do viewer e só voltam pelo reenvio da plataforma.
    // Medido na homolog em 04/10/2026: nenhum item caía no «sem render» antigo
    // (os 10 UPLOADED são todos «error»), então a troca não reclassifica nada.
    case "expired": return "Expirado";
  }
}

export function availabilityColorClass(s: AssetStatus): string {
  switch (s) {
    case "ready": return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    case "queued": return "bg-amber-50 text-amber-700 border border-amber-200";
    case "rendering": return "bg-blue-50 text-blue-700 border border-blue-200 animate-pulse";
    case "preparing": return "bg-sky-50 text-sky-700 border border-sky-200 animate-pulse";
    case "error": return "bg-red-50 text-red-700 border border-red-200";
    case "expired": return "bg-yellow-50 text-yellow-700 border border-yellow-200";
  }
}

// ---- selo do viewer: qual miolo o arquivo abre ----

/** O selo curto ao lado do formato: `Doc` ou `Package`. */
export function viewerKindLabel(kind: ViewerKind): string {
  switch (kind) {
    case "document": return "Doc";
    case "packaging": return "Package";
  }
}

export function viewerKindTitle(kind: ViewerKind): string {
  switch (kind) {
    case "document": return "Abre no viewer de documento";
    case "packaging": return "Abre no viewer de embalagem (separações de cor)";
  }
}

export function plateTypeLabel(type: "process" | "spot" | "technical"): string {
  switch (type) {
    case "process": return "Processo";
    case "spot": return "Spot";
    case "technical": return "Técnico";
  }
}

// ---- F-T.5: lineatura e ângulo de retícula ----
//
// Os dois números saem do XMP do Esko com a precisão que o RIP gravou —
// `137.1600037` de pedido para `136.` de realizado — e a tela mostra os dois
// quando diferem. Formatar aqui, e não no componente, é o que faz o teste do
// painel poder afirmar o texto sem montar React.

/** Quanto dois valores podem diferir e ainda serem "o mesmo número" na tela. */
const SCREEN_VALUE_EPSILON = 0.005;

/** pt-BR, no máximo duas casas, sem zero à toa: 136 · 119,4 · 137,16. */
function screenNumber(value: number): string {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
}

function finite(value: number | null | undefined): number | null {
  // `Number(null)` é 0, e 0 é um ângulo legítimo: sem esta guarda um campo
  // ausente viraria "0 lpi" na tela, que é pior que campo nenhum.
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/** Linhas por polegada, como o XMP grava, ou por centímetro. */
export type ScreenFrequencyUnit = "lpi" | "lpcm";
/** O sentido em que o ângulo é contado na tela. */
export type ScreenAngleSense = "CW" | "CCW";

/**
 * As unidades em que a retícula aparece — escolha de quem lê, não do arquivo.
 *
 * O dado é sempre o mesmo (lpi e CW, como o Esko grava e a medição devolve); a
 * conversão acontece só aqui, na hora de escrever o texto. Por isso o medidor,
 * o painel e o modal trocam juntos sem ninguém pedir de novo ao servidor.
 */
export interface ScreenUnits {
  frequency: ScreenFrequencyUnit;
  angle: ScreenAngleSense;
}

export const DEFAULT_SCREEN_UNITS: ScreenUnits = { frequency: "lpi", angle: "CW" };

/**
 * As unidades lidas do navegador, campo a campo.
 *
 * O valor salvo é de quem tiver escrito naquela chave — outra versão do
 * viewer, a mão no console. O que não for uma das duas opções volta ao padrão,
 * em vez de chegar à tela como unidade que ninguém escolheu.
 */
export function screenUnitsFrom(saved: unknown): ScreenUnits {
  const campos = saved && typeof saved === "object" ? (saved as Record<string, unknown>) : {};
  return {
    frequency: campos.frequency === "lpcm" ? "lpcm" : DEFAULT_SCREEN_UNITS.frequency,
    angle: campos.angle === "CCW" ? "CCW" : DEFAULT_SCREEN_UNITS.angle,
  };
}

const CM_PER_INCH = 2.54;

/**
 * "136 lpi" — e "137,16 → 136 lpi" quando o pedido não foi o realizado.
 *
 * O pedido só aparece quando difere: repetir "136 → 136 lpi" em toda linha das
 * 39 do 1198351 seria ruído, e o que interessa ali é justamente qual delas o
 * RIP não conseguiu realizar como pedido.
 *
 * Em `lpcm` os dois números são divididos por 2,54 antes de comparar — o
 * Esko grava em lpi o que foi pedido em centímetro, e 137,16 lpi volta a ser
 * os 54 lpcm redondos que alguém digitou.
 */
export function formatScreenFrequency(
  frequency: number | null | undefined,
  requested?: number | null,
  unit: ScreenFrequencyUnit = "lpi",
): string {
  const lpi = finite(frequency);
  if (lpi === null) return "";
  const scale = unit === "lpcm" ? 1 / CM_PER_INCH : 1;
  const value = lpi * scale;
  const askedLpi = finite(requested);
  const asked = askedLpi === null ? null : askedLpi * scale;
  const prefix =
    asked !== null && Math.abs(asked - value) > SCREEN_VALUE_EPSILON
      ? `${screenNumber(asked)} → `
      : "";
  return `${prefix}${screenNumber(value)} ${unit}`;
}

/**
 * "82° CW" — o sentido vem de mapa fechado, não do texto do arquivo.
 *
 * `CW`/`CCW` são o vocabulário de prepress e ficam como estão; qualquer outra
 * coisa que apareça no campo é omitida em vez de ir para a tela.
 */
const ANGLE_DIRECTIONS: Record<string, ScreenAngleSense> = { CW: "CW", CCW: "CCW" };

/**
 * O mesmo ângulo contado no outro sentido.
 *
 * Uma grade de ponto redondo ou quadrado se repete a cada 90°, e é nessa faixa
 * que o Esko declara e que a medição devolve (`mod 90`): ali 82,5° CW e 7,5°
 * CCW são a mesma retícula, e a conta é `90 − θ`. Um arquivo que declara acima
 * de 90° está contando até 180° — ponto elíptico, que só se repete a cada meia
 * volta —, e então a conta é `180 − θ`; dobrar em 90 trocaria o eixo do ponto.
 * O resultado que arredonda para a volta inteira vira 0: "90°" leria como
 * outra retícula.
 */
function oppositeSenseAngle(angle: number): number {
  const period = angle >= 90 ? 180 : 90;
  const mirrored = (((period - angle) % period) + period) % period;
  return mirrored >= period - SCREEN_VALUE_EPSILON ? 0 : mirrored;
}

export function formatScreenAngle(
  angle: number | null | undefined,
  direction?: string | null,
  requested?: number | null,
  display?: ScreenAngleSense,
): string {
  const declared = finite(angle);
  if (declared === null) return "";
  const source = ANGLE_DIRECTIONS[String(direction ?? "").trim().toUpperCase()];
  // Sem sentido conhecido no arquivo não há de onde converter: o número sai
  // como veio, e sem rótulo — inventar um sentido seria pior.
  const convert = Boolean(source && display && display !== source);
  const value = convert ? oppositeSenseAngle(declared) : declared;
  const askedRaw = finite(requested);
  const asked = askedRaw !== null && convert ? oppositeSenseAngle(askedRaw) : askedRaw;
  const prefix =
    asked !== null && Math.abs(asked - value) > SCREEN_VALUE_EPSILON
      ? `${screenNumber(asked)}° → `
      : "";
  const sense = source ? (display ?? source) : null;
  return `${prefix}${screenNumber(value)}°${sense ? ` ${sense}` : ""}`;
}
